import { Injectable } from "@angular/core";
import {
  Resolve,
  RouterStateSnapshot,
  ActivatedRouteSnapshot,
} from "@angular/router";
import { Observable, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import {
  Meta,
  Title,
  TransferState,
  makeStateKey,
  StateKey,
} from "@angular/platform-browser";

import { MetaTagService } from "app/services/meta-tag.service";
import { MetaTag } from "app/shared/mate_tag";

@Injectable({ providedIn: "root" })
export class MetaTagResolver implements Resolve<MetaTag | null> {
  // استخدم string كمفتاح واضح للكاش
  private metaTagCache = new Map<string, MetaTag | null>();

  constructor(
    private metaTagService: MetaTagService,
    private title: Title,
    private transferState: TransferState,
    private meta: Meta
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<MetaTag | null> {
    const pageName = route.data["pageName"] as string;
    const langRaw = route.paramMap.get("lang"); // ممكن تكون null
    const lang = this.normalizeLang(langRaw); // 'EN' | 'AR' | ...
    const cacheKey = `${pageName}__${lang}`;
    const TS_KEY: StateKey<MetaTag | null> = makeStateKey<MetaTag | null>(
      `metaTag_${cacheKey}`
    );

    // 1) In-memory cache
    if (this.metaTagCache.has(cacheKey)) {
      const res = this.metaTagCache.get(cacheKey)!;
      this.updateMetaTags(res, lang, state.url);
      return of(res);
    }

    // 2) TransferState
    if (this.transferState.hasKey(TS_KEY)) {
      const res = this.transferState.get(TS_KEY, null);
      this.transferState.remove(TS_KEY);
      this.metaTagCache.set(cacheKey, res);
      this.updateMetaTags(res, lang, state.url);
      return of(res);
    }

    // 3) Fetch
    return this.metaTagService.getMetaTags().pipe(
      switchMap((all) => {
        const res =
          (all || []).find((m: MetaTag) => m.pageName === pageName) || null;
        this.transferState.set(TS_KEY, res);
        this.metaTagCache.set(cacheKey, res);
        this.updateMetaTags(res, lang, state.url);
        return of(res);
      })
    );
  }

  /** يطابق مفاتيح اللغة في الـJSON (EN/AR/FR/TR/RU). Default: EN */
  private normalizeLang(lang: string | null): "EN" | "AR" | "FR" | "TR" | "RU" {
    const L = (lang || "en").toUpperCase();
    if (L === "AR" || L === "FR" || L === "TR" || L === "RU") return L as any;
    return "EN";
  }

  private pick(
    obj: Record<string, any> | undefined,
    lang: string,
    fallbacks: string[] = ["EN"]
  ): string | undefined {
    if (!obj) return undefined;
    const L = lang.toUpperCase();
    if (obj[L]) return obj[L];
    for (const fb of fallbacks) if (obj[fb]) return obj[fb];
    // آخر حل: أول قيمة موجودة
    const firstKey = Object.keys(obj)[0];
    return firstKey ? obj[firstKey] : undefined;
  }

  private updateMetaTags(
    metaTagRes: MetaTag | null,
    lang: string,
    url: string
  ) {
    // قيم افتراضية آمنة
    const defaultSiteName = "Zirve International";
    const defaultImage = "https://msr.zirvecompany.net/images/logo30.png";

    const pageTitle =
      this.pick(metaTagRes?.pageTitle as any, lang) || defaultSiteName;
    const metaTitle =
      this.pick(metaTagRes?.metaTitle as any, lang) || pageTitle;
    const metaDesc = this.pick(metaTagRes?.metaDescription as any, lang) || "";

    // <title>
    this.title.setTitle(pageTitle);

    // description (name فقط)
    this.meta.updateTag({ name: "description", content: metaDesc });

    // og: tags
    this.meta.updateTag({ property: "og:title", content: metaTitle });
    this.meta.updateTag({ property: "og:description", content: metaDesc });
    this.meta.updateTag({ property: "og:type", content: "website" });
    this.meta.updateTag({ property: "og:site_name", content: defaultSiteName });
    this.meta.updateTag({ property: "og:url", content: this.absUrl(url) });
    this.meta.updateTag({ property: "og:image", content: defaultImage });

    // twitter cards
    this.meta.updateTag({
      name: "twitter:card",
      content: "summary_large_image",
    });
    this.meta.updateTag({ name: "twitter:title", content: metaTitle });
    this.meta.updateTag({ name: "twitter:description", content: metaDesc });
    this.meta.updateTag({ name: "twitter:image", content: defaultImage });

    // locale (OG)
    const ogLocale =
      lang === "AR"
        ? "ar_AR"
        : lang === "TR"
        ? "tr_TR"
        : lang === "FR"
        ? "fr_FR"
        : lang === "RU"
        ? "ru_RU"
        : "en_US";
    this.meta.updateTag({ property: "og:locale", content: ogLocale });

    // robots (دمج القيم وتحديث واحد)
    const flags: string[] = [];
    if (metaTagRes?.noIndex) flags.push("noindex");
    if (metaTagRes?.noFollow) flags.push("nofollow");
    const robots = flags.length ? flags.join(",") : "index,follow";
    this.meta.updateTag({ name: "robots", content: robots });
  }

  /** يبني URL مطلق للـog:url (يفضل وقت SSR) */
  private absUrl(path: string): string {
    if (!path) return "/";
    try {
      // بدّل الدومين حسب بيئتك
      const base =
        typeof window !== "undefined"
          ? window.location.origin
          : "https://www.zirvecompany.net";
      return new URL(path, base).toString();
    } catch {
      return path;
    }
  }
}
